import type {
  BillingDashboardApiItem,
  BillingDashboardInvoicedVsPaidPoint,
  BillingOutstandingInvoiceApiItem,
} from 'src/redux/billing/billing.types';
import type { BillingFinancialOverview, BillingMonthlySeriesPoint, PastDueInvoiceRow } from './billing-dashboard.types';

function formatMonthLabel(ym: string): string {
  const parts = ym.split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

function mapInvoicedVsPaidPoints(points: BillingDashboardInvoicedVsPaidPoint[]): BillingMonthlySeriesPoint[] {
  return points.map((p) => ({
    monthLabel: formatMonthLabel(p.month),
    invoiced: p.invoiced,
    collected: p.paid,
  }));
}

export function mapBillingDashboardApiToOverview(
  item: BillingDashboardApiItem,
  overdueAmountFromInvoices: number
): BillingFinancialOverview {
  const barSeries = mapInvoicedVsPaidPoints(item.charts.invoiced_vs_paid_last_3_months ?? []);
  const lineSeries = mapInvoicedVsPaidPoints(item.charts.invoiced_vs_paid_mom ?? []);
  const m = item.metrics;

  return {
    totalBilledCurrentPeriod: m.amount_invoiced ?? 0,
    totalCollected: m.amount_collected ?? 0,
    outstandingBalance: m.outstanding_amount ?? 0,
    overdueAmount: overdueAmountFromInvoices,
    priorMonthBilled: 0,
    priorMonthCollected: 0,
    currentMonthLabel: '',
    priorMonthLabel: '',
    invoicedPercentChangeVsPrior: m.percent_change_invoiced_mom ?? null,
    collectedPercentChangeVsPrior: null,
    monthlySeries: barSeries,
    monthlySeriesTrend: lineSeries.length > 0 ? lineSeries : undefined,
    snapshotUpdatedAt: item.snapshot?.updated_at ?? null,
  };
}

function daysOverdueFromDueDate(dueDateIso: string): number {
  const due = new Date(dueDateIso);
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffMs = today.getTime() - due.getTime();
  return diffMs > 0 ? Math.floor(diffMs / 86400000) : 0;
}

export function mapOutstandingInvoiceApiToRow(row: BillingOutstandingInvoiceApiItem): PastDueInvoiceRow {
  return {
    id: row.id,
    invoiceNumber: row.ext_invoice_number?.trim() ? row.ext_invoice_number : '—',
    clientName: row.company_name?.trim() ? row.company_name : '—',
    dueDate: row.due_date,
    daysOverdue: daysOverdueFromDueDate(row.due_date),
    amount: row.remaining_balance ?? 0,
  };
}
